import { useState, useMemo } from 'react';
import {
  COLOR_CSS, COLOR_LABELS, ELEMENT_CSS, ELEMENT_LABELS,
  RESONANCE_LABELS,
} from '../data/attributes';
import type { Stone, ElementType, StoneColor, StoneType } from '../types';
import './StoneSearchModal.css';

export interface SearchFilters {
  elements: ElementType[];
  types: StoneType[];
  colors: StoneColor[];
  attrLabels: string[];
}

const EMPTY_FILTERS: SearchFilters = {
  elements: [],
  types: [],
  colors: [],
  attrLabels: [],
};

/* ===== 白色属性选项 ===== */
const WHITE_ATTR_TAGS = [
  { key: '会心', label: '会心', elem: null as ElementType | null },
  { key: '专精', label: '专精', elem: null as ElementType | null },
  { key: '调息', label: '调息', elem: null as ElementType | null },
  { key: '元御', label: '元御', elem: null as ElementType | null },
];

/* ===== 元素属性选项（增强/抵抗） ===== */
const ELEMENT_ATTR_TAGS: { key: string; label: string; elem: ElementType }[] = [];
for (const elem of ['fire', 'ice', 'wood', 'thunder'] as ElementType[]) {
  for (const suffix of ['增强', '抵抗']) {
    const label = `${ELEMENT_LABELS[elem]}${suffix}`;
    ELEMENT_ATTR_TAGS.push({ key: label, label, elem });
  }
}

/* ===== 灵蕴共鸣选项 ===== */
const RESONANCE_TAGS = Object.entries(RESONANCE_LABELS).map(([key, label]) => ({
  key,
  label,
  elem: (key.startsWith('fire') ? 'fire' :
    key.startsWith('ice') ? 'ice' :
    key.startsWith('wood') ? 'wood' : 'thunder') as ElementType,
}));

interface StoneSearchModalProps {
  stones: Stone[];
  currentFilters: SearchFilters;
  onConfirm: (filters: SearchFilters) => void;
  onClose: () => void;
}

/** 将搜索过滤条件转成可读标签列表 */
function filtersToTags(filters: SearchFilters): string[] {
  const tags: string[] = [];
  filters.elements.forEach(e => tags.push(`元素:${ELEMENT_LABELS[e]}`));
  filters.types.forEach(t => tags.push(t === 'skill' ? '机巧石' : '匠心石'));
  filters.colors.forEach(c => tags.push(`颜色:${COLOR_LABELS[c]}`));
  filters.attrLabels.forEach(a => tags.push(a));
  return tags;
}

export function StoneSearchModal({
  stones,
  currentFilters,
  onConfirm,
  onClose,
}: StoneSearchModalProps) {
  const [filters, setFilters] = useState<SearchFilters>(currentFilters);

  const activeTags = useMemo(() => filtersToTags(filters), [filters]);

  /* ---------- 辅助：切换数组元素 ---------- */
  const toggleArray = <T,>(arr: T[], item: T): T[] => {
    if (arr.includes(item)) {
      return arr.filter(x => x !== item);
    }
    if (arr.length === 0) return [item];
    // 单选模式：替换
    return [item];
  };

  const toggleMulti = <T,>(arr: T[], item: T): T[] => {
    if (arr.includes(item)) {
      return arr.filter(x => x !== item);
    }
    return [...arr, item];
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
  };

  const handleConfirm = () => {
    onConfirm(filters);
    onClose();
  };

  const handleRemoveTag = (tag: string) => {
    setFilters(prev => {
      const next = { ...prev };
      if (tag.startsWith('元素:')) {
        const elem = tag.replace('元素:', '');
        const key = (Object.entries(ELEMENT_LABELS) as [ElementType, string][])
          .find(([, v]) => v === elem)?.[0];
        if (key) next.elements = prev.elements.filter(e => e !== key);
      } else if (tag === '机巧石') {
        next.types = prev.types.filter(t => t !== 'skill');
      } else if (tag === '匠心石') {
        next.types = prev.types.filter(t => t !== 'craft');
      } else if (tag.startsWith('颜色:')) {
        const col = tag.replace('颜色:', '');
        const key = (Object.entries(COLOR_LABELS) as [StoneColor, string][])
          .find(([, v]) => v === col)?.[0];
        if (key) next.colors = prev.colors.filter(c => c !== key);
      } else {
        next.attrLabels = prev.attrLabels.filter(a => a !== tag);
      }
      return next;
    });
  };

  const resultCount = useMemo(() => {
    let list = stones;
    if (filters.elements.length > 0) {
      list = list.filter(s => filters.elements.includes(s.element));
    }
    if (filters.types.length > 0) {
      list = list.filter(s => filters.types.includes(s.type));
    }
    if (filters.colors.length > 0) {
      list = list.filter(s => filters.colors.includes(s.color));
    }
    if (filters.attrLabels.length > 0) {
      list = list.filter(s =>
        s.attributes.some(a => filters.attrLabels.includes(a.label))
      );
    }
    return list.length;
  }, [stones, filters]);

  return (
    <div className="search-modal-overlay" onClick={onClose}>
      <div className="search-modal" onClick={e => e.stopPropagation()}>
        {/* 头部 */}
        <div className="search-modal-header">
          <h3>
            🔍 搜索石头
            <span className="filter-count">
              {activeTags.length > 0 ? `已选 ${activeTags.length} 项` : '全部石头'}
            </span>
          </h3>
          <button className="search-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="search-modal-body">
          {/* 已选条件 */}
          <div className={`search-active-filters ${activeTags.length === 0 ? 'empty' : ''}`}>
            {activeTags.length > 0 ? (
              activeTags.map(tag => (
                <span key={tag} className="active-filter-tag">
                  {tag}
                  <button className="remove-filter" onClick={() => handleRemoveTag(tag)}>✕</button>
                </span>
              ))
            ) : (
              '未选择任何筛选条件，将显示全部石头'
            )}
          </div>

          {/* 元素类型 */}
          <div className="search-field">
            <span className="search-field-label">
              属性前缀（元素类型）
              <span className="label-hint">点击切换</span>
            </span>
            <div className="search-element-grid">
              {(Object.entries(ELEMENT_LABELS) as [ElementType, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className={`search-elem-btn ${filters.elements.includes(key) ? 'active' : ''}`}
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    elements: toggleArray(prev.elements, key),
                  }))}
                  style={filters.elements.includes(key) ? {
                    borderColor: ELEMENT_CSS[key],
                    boxShadow: `0 0 8px ${ELEMENT_CSS[key]}44`,
                    background: `${ELEMENT_CSS[key]}18`,
                  } : {}}
                >
                  {key === 'fire' ? '🔥' : key === 'ice' ? '❄️' : key === 'wood' ? '🌿' : '⚡'} {label}
                </button>
              ))}
            </div>
          </div>

          {/* 石头类型 */}
          <div className="search-field">
            <span className="search-field-label">石头类型</span>
            <div className="search-type-toggle">
              {([['skill', '机巧石'], ['craft', '匠心石']] as [StoneType, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className={`search-type-btn ${filters.types.includes(key) ? 'active' : ''}`}
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    types: toggleArray(prev.types, key),
                  }))}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 颜色 */}
          <div className="search-field">
            <span className="search-field-label">颜色</span>
            <div className="search-color-grid">
              {(Object.entries(COLOR_LABELS) as [StoneColor, string][]).map(([key, label]) => (
                <button
                  key={key}
                  className={`search-color-btn ${filters.colors.includes(key) ? 'active' : ''}`}
                  onClick={() => setFilters(prev => ({
                    ...prev,
                    colors: toggleArray(prev.colors, key),
                  }))}
                  style={filters.colors.includes(key) ? {
                    borderColor: COLOR_CSS[key],
                    background: `${COLOR_CSS[key]}18`,
                  } : {}}
                >
                  <span className="search-color-dot" style={{ background: COLOR_CSS[key] }} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 白色属性 */}
          <div className="search-field">
            <span className="search-field-label">白色属性</span>
            <div className="search-attr-tags">
              {WHITE_ATTR_TAGS.map(opt => {
                const active = filters.attrLabels.includes(opt.label);
                return (
                  <button
                    key={opt.key}
                    className={`search-attr-tag ${active ? 'active' : ''}`}
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      attrLabels: toggleMulti(prev.attrLabels, opt.label),
                    }))}
                    style={{ borderLeftColor: '#e8e8e8' }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 元素增强/抗性 */}
          <div className="search-field">
            <span className="search-field-label">元素增强 / 抵抗</span>
            <div className="search-attr-tags">
              {ELEMENT_ATTR_TAGS.map(opt => {
                const active = filters.attrLabels.includes(opt.label);
                return (
                  <button
                    key={opt.key}
                    className={`search-attr-tag ${active ? 'active' : ''}`}
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      attrLabels: toggleMulti(prev.attrLabels, opt.label),
                    }))}
                    style={{ borderLeftColor: ELEMENT_CSS[opt.elem] }}
                  >
                    <span className="tag-icon">
                      {opt.elem === 'fire' ? '🔥' : opt.elem === 'ice' ? '❄️' : opt.elem === 'wood' ? '🌿' : '⚡'}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 灵蕴共鸣 */}
          <div className="search-field">
            <span className="search-field-label">特效词条（灵蕴共鸣）</span>
            <div className="search-attr-tags">
              {RESONANCE_TAGS.map(opt => {
                const active = filters.attrLabels.includes(opt.label);
                return (
                  <button
                    key={opt.key}
                    className={`search-attr-tag ${active ? 'active' : ''}`}
                    onClick={() => setFilters(prev => ({
                      ...prev,
                      attrLabels: toggleMulti(prev.attrLabels, opt.label),
                    }))}
                    style={{ borderLeftColor: ELEMENT_CSS[opt.elem] }}
                  >
                    <span className="tag-icon">
                      {opt.elem === 'fire' ? '🔥' : opt.elem === 'ice' ? '❄️' : opt.elem === 'wood' ? '🌿' : '⚡'}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* 底部 */}
        <div className="search-modal-footer">
          <button className="btn-search-clear" onClick={handleClear}>
            🗑 重置
          </button>
          <button className="btn-search-confirm" onClick={handleConfirm}>
            确定筛选 {activeTags.length > 0 ? `（匹配 ${resultCount} 个）` : '（全部）'}
          </button>
        </div>
      </div>
    </div>
  );
}
